import React, { useState } from "react";
import { MONEY_TRANSFER_DIRECTIVE_TYPES, ICONS, ACTION_DISPLAY_TYPES, MONEY_TRANSFER_FIELD_IDS } from "../../../constants/enums";
import { AvailableAction } from "../../../types/api/moneyTransfer";
import { PAGES } from "../../../constants/localization";
import i18n from "../../../i18n/config";
import useClickExternalLink from "../../../hooks/useClickExternalLink";
import ENV from "../../../constants/environment";
import { ROUTES } from "../../../constants/api";
import { PostApiData } from "../../../api/general";
import { MoneyTransferCancelRequest, MoneyTransferCancelResponse } from "../../../types/api/card";
import { TransactionDetailsType } from "../../../types/components";

// components
import Alert from "../../Common/Alerts/Alert";
import ActionLink from "../../Common/ActionLink";
import Modal from "../../Common/Modal";
import Icon from "../../Common/Icon";

const TransactionAvailableActions = ({ transactionDetails, handleGetTransactionDetails, moneyTransferId }: { transactionDetails: TransactionDetailsType, handleGetTransactionDetails: () => void, moneyTransferId?: string }) => {
  const openExternalLinksModal = useClickExternalLink();

  const receiverName = transactionDetails?.displayFields?.find(field => field.id === MONEY_TRANSFER_FIELD_IDS.TRANSACTION_RECEIVER_NAME)?.formattedValue;
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [cancelModalLoading, setCancelModalLoading] = useState(false);
  const [cancelModalContent, setCancelModalContent] = useState(`${PAGES.MY_CARD.TRANSACTION_DETAILS.CANCEL_MODAL_CONTENT}${receiverName}`);
  const [cancelModalTitle, setCancelModalTitle] = useState(<span>{i18n.t("COMMON.CANCEL_TRANSACTION")}</span>);
  const [cancelModalActions, setCancelModalActions] = useState([{
    title: "Go back",
    onClick: () => setOpenCancelModal(false),
    displayType: ACTION_DISPLAY_TYPES.LINK,
    className: "bold",
    disabled: cancelModalLoading
  },
  {
    title: PAGES.MY_CARD.TRANSACTION_DETAILS.CANCEL_MODAL_ACTION_TITLE,
    onClick: () => { handleCancelTransaction(); },
    displayType: ACTION_DISPLAY_TYPES.PRIMARY,
    className: "warning bold",
    disabled: cancelModalLoading
  }]);
  const [cancelIsSuccessful, setCancelIsSuccessful] = useState(false);

  /**
   * Handle closing of Cancel Transaction modal after successful cancellation
   * If cancellation was successful, refresh transaction details to reflect change
   */
  const handleClose = () => {
    if (cancelIsSuccessful) {
      handleGetTransactionDetails();
    }
    setOpenCancelModal(false);
  }

  /**
 * Handle Cancel Transaction button click
 * Depending on if API call is successful or not, Modal title,content, and actions should change further guide user
 */
  const handleCancelTransaction = async () => {
    // Without an id the cancel request cannot succeed, so decline it rather than posting a placeholder
    if (!moneyTransferId) {
      setCancelModalTitle(<><Icon icon={ICONS.WARNING_INFO} /> <span>{i18n.t("COMMON.TRANSACTION_NOT_CANCELLED")}</span></>);
      setCancelModalContent(i18n.t("COMMON.ERROR"));
      // Retrying cannot help without an id, so leave a single Close action rather than an enabled confirm
      setCancelModalActions([
        {
          ...cancelModalActions[1],
          className: "bold",
          title: "Close",
          onClick: () => handleClose(),
        }
      ]);
      return;
    }

    setCancelModalLoading(true);
    setCancelModalActions([
      { ...cancelModalActions[0], disabled: true },
      { ...cancelModalActions[1], disabled: true }
    ])

    try {
      const url = ROUTES.MONEY_TRANSFER.CANCEL_TRANSACTION();
      const response = await PostApiData<MoneyTransferCancelRequest, MoneyTransferCancelResponse>(url, { moneyTransferId });

      // If successful, change modal title, content, and actions
      setCancelModalTitle(<><Icon icon={ICONS.GREEN_CHECK} /> <span>{i18n.t("COMMON.TRANSACTION_CANCELLED")}</span></>);
      setCancelModalContent(response.message);
      setCancelModalActions([
        {
          ...cancelModalActions[1],
          className: "bold",
          title: "Close",
          onClick: () => handleClose(),
        }
      ])
      setCancelModalLoading(false);
      setCancelIsSuccessful(true);
    } catch (err) {
      setCancelModalContent(err?.data?.errors[0].errorMessage);
      setCancelModalTitle(<><Icon icon={ICONS.WARNING_INFO} /> <span>{i18n.t("COMMON.TRANSACTION_NOT_CANCELLED")}</span></>);
      setCancelModalActions([
        { ...cancelModalActions[0] },
        {
          ...cancelModalActions[1],
          className: "bold",
          title: "Try again"
        }
      ]);
      setCancelModalLoading(false);
    }
  }

  /**
   * Returns <a /> or React component based on directive type
   * @param {object} directive object containing id, text, type, url
   */
  const createDirectiveElement = (directive) => {
    const { text, type, url } = directive;

    if (type === MONEY_TRANSFER_DIRECTIVE_TYPES.EXTERNAL_LINK) {
      return (
        <a
          key={directive.id}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => openExternalLinksModal(e, url)}
          title={i18n.t("COMMON.EXTERNAL_LINK_TITLE")}
          className="">
          {text}
        </a>
      )
    } else if (type === MONEY_TRANSFER_DIRECTIVE_TYPES.CANCEL_MONEY_TRANSFER) {
      return (
        <ActionLink
          text={text}
          clickFunc={() => setOpenCancelModal(true)}
          key={directive.id}
          classes={"bold"}
        />
      )
    } else if (type === MONEY_TRANSFER_DIRECTIVE_TYPES.SUPPORT) {
      return (
        <ActionLink
          text={text}
          href={`${ENV.BASE_NAVIGATOR_URL}/support`}
          key={directive.id}
          classes={"bold"}
        />
      )
    }
  }

  /**
   * Render single <Alert /> component for each available action
   * @param action AvailableAction object
   * @param actionArr 
   * @returns 
   */
  const renderAction = (action: AvailableAction, actionArr: string[]) => {
    return (
      <Alert
        alertClasses={"alert-info spacing-top-small flex-row font-regular"}
        isHTML={true}
        icon={ICONS.INFO}
        iconClasses={"spacing-right-tiny"}
        key={action.title}
      >
        <p className="bold">{action.title}</p>

        {actionArr.map(item => {
          const directiveObj = action.directives.find(directive => `{${directive.id}}` === item);
          return directiveObj
            ? <>
              {createDirectiveElement(directiveObj)}
            </>
            : <span key={item} dangerouslySetInnerHTML={{ __html: item }} />;
        })}
      </Alert>
    )
  }

  /**
     * Render <Alert /> component with appropriate text and directives 
     */
  const renderAvailableActions = () => {
    const actions = (transactionDetails.availableActions ?? []).map((action: AvailableAction) => {
      const pattern = /\{([^}]+)\}/g;
      const matches = action.text.match(pattern);

      let splitString = action.text
      let arr: string[] = [];

      if (matches) {
        matches.forEach((match: string) => {
          const temp = splitString.split(match);

          arr.push(temp[0]);
          arr.push(match);
          splitString = temp[1];
        })
      } else {
        arr = [action.text];
      }

      const hasCancelDirective = action.directives.find(directive => directive.type === MONEY_TRANSFER_DIRECTIVE_TYPES.CANCEL_MONEY_TRANSFER);

      if (hasCancelDirective && cancelIsSuccessful) {
        return null;
      }

      return renderAction(action, arr);
    })

    return actions;
  }


  return (
    <>
      {renderAvailableActions()}

      <Modal
        open={openCancelModal}
        onClose={handleClose}
        className="modal-title-flex"
        title={cancelModalTitle}
        actions={cancelModalActions}
        content={cancelModalContent}
        contentLoading={cancelModalLoading}
      />

    </>
  );
}

export default TransactionAvailableActions;